import React, { useState } from 'react';
import { AccessibilityRole } from 'react-native';

import IconCancel from '../../assets/svg/IconCancel';
import { isWebPlatform } from '../../boot/utils';
import { AnalyticsEvent } from '../../constants/AnalyticsEvent';
import ElementType from '../../constants/ElementType';
import FeatureFlags from '../../constants/FeatureFlags';
import { MktType } from '../../constants/MktType';
import { globalNormalDialogProxy } from '../../elements/Dialog/GlobalNormalDialog';
import ItemLabel from '../../elements/ItemLabel';
import Price from '../../elements/Price';
import Touchable from '../../elements/Touchable';
import { useTranslation } from '../../i18n';
import { LogEventPayload } from '../../redux/actions/analyticsLog';
import { DeleteCartItemPayload } from '../../redux/actions/deleteCartItemActions';
import { UpdateSelectedCartPayload } from '../../redux/actions/selectCartActions';
import type { UpdateUseDiscountsActionPayload } from '../../redux/actions/updateUseDiscountsActions';
import { ItemTypes } from '../../types/Common';
import { getIsTW } from '../../utils/countryInfo';
import formatSkuPattern from '../../utils/formatSkuPattern';
import * as navigationService from '../../utils/navigationService';
import routeUtils from '../../utils/routes';
import CardCounter from './CardCounterContainer';
import DeliveryTags from './DeliveryTags';
import { S, styles } from './styles';

interface Props {
  itemLength: number;
  updateUseDiscount: (payload: UpdateUseDiscountsActionPayload) => void;
  colorText?: string;
  deleteCartItem: (payload: DeleteCartItemPayload) => void;
  userSubmitId: string;
  imageUrl?: string;
  skuImageUrl?: string;
  isCheck?: boolean;
  price?: number | string;
  sizeText?: string;
  title?: string;
  updateSelectedCart: (payload: UpdateSelectedCartPayload) => void;
  vid: string;
  hasCVSCOD: boolean;
  hasCVS: boolean;
  hasHomeCOD: boolean;
  itemHeight: number;
  index: number;
  logEvent: (payload: LogEventPayload) => void;
  layoutName?: string;
  source?: string;
  ribbonText: string;
  isOutOfStock?: boolean;
  isDiscount: boolean;
}

const CheckoutItemCard: React.FC<Props> = ({
  colorText = '',
  deleteCartItem,
  updateUseDiscount,
  userSubmitId,
  imageUrl = '',
  skuImageUrl = '',
  price = '',
  sizeText = '',
  title = '',
  vid,
  hasCVSCOD,
  hasCVS,
  hasHomeCOD,
  index,
  logEvent,
  layoutName,
  source,
  isOutOfStock,
  isDiscount,
  itemLength,
}) => {
  const [itemType] = useState(ItemTypes.USER_SUBMIT);

  const { t } = useTranslation();

  const navigateTo = (navigateFunction: Function) => {
    return navigateFunction('ItemPage', {
      id: userSubmitId,
    });
  };

  const handlePressItem = () => {
    logEvent({
      logEvent: AnalyticsEvent.Click,
      data: {
        id: userSubmitId,
        itemType: itemType,
        pitNumber: index + 1,
        mktType: MktType.Image,
        elementType: ElementType.CartContentItem,
        vid,
        layoutName,
        source,
      },
    });

    navigateTo(navigationService.navigate);
  };

  const deleteItem = (virtualProductID: string) => {
    deleteCartItem({
      virtualProductIDs: [virtualProductID],
    });
  };

  const handleRemovePressed = () => {
    logEvent({
      logEvent: AnalyticsEvent.CartRemove,
      data: {
        vid,
        isOutOfStock,
      },
    });

    if (isOutOfStock) {
      deleteItem(vid);

      return;
    }

    const dialogImageUrl = skuImageUrl || imageUrl || '';

    globalNormalDialogProxy.open({
      positiveText: t('delete'),
      negativeText: t('cancel'),
      contentText: t('confirm-remove-cart-item'),
      dialogImageUrl,
      handleDialogPositiveAction: () => {
        if (itemLength === 1) {
          updateUseDiscount({
            currentDiscount: undefined,
          });
        }
        deleteItem(vid);
      },
    });
  };

  const resizedUrl = skuImageUrl || imageUrl || '';

  const touchableProps = isOutOfStock
    ? {}
    : {
        accessibilityRole: 'link' as AccessibilityRole,
        href: navigateTo(routeUtils.compileToPath),
      };

  return (
    <S.ContentCard
      animation="fadeIn"
      duration={300}
      useNativeDriver={!isWebPlatform}
      testID={`cartItem_${index}`}
      nativeID={`cartItem`}
    >
      <S.MainContentOfCard>
        <S.ImageField>
          <Touchable
            disabled={isOutOfStock}
            key="touch-image-field"
            withoutFeedback
            onPress={handlePressItem}
            {...touchableProps}
          >
            <>
              <S.ImageSoldWrapper visible={isOutOfStock}>
                <S.ImageSoldText>{t('sold')}</S.ImageSoldText>
              </S.ImageSoldWrapper>
              <S.ItemImage source={{ uri: resizedUrl }} />
            </>
          </Touchable>
        </S.ImageField>
        <S.CancelButton
          accessibilityRole="button"
          testID={`removeItemBtn_${index}`}
          key="touch-cancel-button"
          onPress={handleRemovePressed}
        >
          <IconCancel size={18} />
        </S.CancelButton>
        <S.DetailField>
          <S.DescriptionField>
            <S.TitleField>
              <S.TitleText disabled={isOutOfStock} numberOfLines={1}>
                {title}
              </S.TitleText>
              <Touchable
                disabled={isOutOfStock}
                key="touch-detail-field"
                testID="touch-detail-field"
                style={{
                  width: '80%',
                  height: '100%',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  backgroundColor: 'transparent',
                }}
                onPress={handlePressItem}
                {...touchableProps}
              />
            </S.TitleField>
            <S.SKUField>
              <S.SKUText numberOfLines={1} disabled={isOutOfStock}>
                {formatSkuPattern(colorText, sizeText)}
              </S.SKUText>
            </S.SKUField>
            {FeatureFlags.GROWTH_715_OVERSTOCK_CONTAINER && (
              <ItemLabel text={t('fast-delivery-text')} />
            )}
          </S.DescriptionField>
        </S.DetailField>
      </S.MainContentOfCard>
      {FeatureFlags.CX_2310_MULTI_PIECE_DISCOUNT && (
        <S.MultipleDiscountHint>
          <S.MultipleDiscountHintTitle>
            <S.MultipleDiscountHintText isDiscount={isDiscount}>
              {t(
                `check-multi-piece-discount.${
                  isDiscount ? 'titleForConform' : 'titleForNotConform'
                }`
              )}
            </S.MultipleDiscountHintText>
          </S.MultipleDiscountHintTitle>
          <S.MultipleDiscountHintText isDiscount={isDiscount}>
            {t('check-multi-piece-discount.text', { discount: 50 })}
          </S.MultipleDiscountHintText>
        </S.MultipleDiscountHint>
      )}
      <S.PaymentField>
        <S.PriceField>
          <Price
            price={price}
            currencyStyle={styles.currencyText}
            priceStyle={styles.priceText}
            containerStyle={[
              styles.priceContainer,
              isOutOfStock && styles.hide,
            ]}
          />
          <S.TooSlowText visible={isOutOfStock}>{t('too-slow')}</S.TooSlowText>
        </S.PriceField>
        <S.CounterField disabled={isOutOfStock}>
          <CardCounter vid={vid} />
        </S.CounterField>
      </S.PaymentField>
      {getIsTW() && (
        <DeliveryTags
          isOutOfStock={!!isOutOfStock}
          hasCVS={hasCVS}
          hasHomeCOD={hasHomeCOD}
          hasCVSCOD={hasCVSCOD}
        />
      )}
    </S.ContentCard>
  );
};

export default CheckoutItemCard;
